import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, formatDate, imgUrl } from "@/lib/api";
import { DemoBadge } from "@/components/MarketBits";

const FILTERS = [["pending", "Waiting"], ["approved", "Live"], ["rejected", "Not approved"], ["removed", "Taken down"], ["", "All"]];

export default function MarketReview() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [sellers, setSellers] = useState([]);

  const load = useCallback(() => {
    api.get("/admin-market/listings", { params: { status: status || undefined } }).then(({ data }) => setItems(data)).catch((e) => toast.error(errMsg(e)));
    api.get("/admin-market/sellers").then(({ data }) => setSellers(data)).catch(() => {});
  }, [status]);
  useEffect(() => {
    load();
  }, [load]);

  const review = async (id, action) => {
    const reason = action === "approve" ? "" : window.prompt(action === "reject" ? "Reason (sent to the seller):" : "Reason (optional):") ?? null;
    if (reason === null) return;
    try {
      await api.post(`/admin-market/listings/${id}/review`, { action, reason });
      toast.success(action === "approve" ? "Approved: live for 30 days" : action === "reject" ? "Rejected and credit returned" : "Taken down");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const btn = "px-3 py-1.5 text-xs border";
  return (
    <div data-testid="admin-market-panel">
      <h2 className="text-2xl sm:text-3xl text-[#1a261f] mb-2">Market</h2>
      <p className="text-sm text-[#5a665e] mb-6">{sellers.filter((s) => s.plan_active && !s.is_demo).length} paying sellers · {sellers.filter((s) => s.is_demo).length} demo sellers</p>
      <div className="flex flex-wrap gap-2 mb-5">
        {FILTERS.map(([k, v]) => (
          <button key={v} onClick={() => setStatus(k)} data-testid={`admin-market-filter-${v.toLowerCase().replace(/ /g, "-")}`}
            className={`${btn} ${status === k ? "bg-[#1f402b] text-white border-[#1f402b]" : "border-[#e2dcd4] bg-white"}`}>{v}</button>
        ))}
      </div>
      <div className="space-y-3">
        {items.length === 0 && <p className="border border-[#e2dcd4] bg-white p-6 text-sm text-[#5a665e]" data-testid="admin-market-empty">Nothing here.</p>}
        {items.map((l) => (
          <div key={l.id} className="border border-[#e2dcd4] bg-white p-4 flex flex-col sm:flex-row gap-4" data-testid={`admin-market-item-${l.id}`}>
            {l.images?.[0] && <img src={imgUrl(l.images[0])} alt="" className="w-full sm:w-28 h-24 object-cover" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-[#1a261f]">{l.title}</p>
                {l.is_demo && <DemoBadge />}
              </div>
              <p className="font-mono-k text-[11px] text-[#5a665e] mt-1">{l.seller?.business_name} · {l.category} · {l.price || "no price"} · {l.status}{l.expires_at && l.status === "approved" ? ` until ${formatDate(l.expires_at)}` : ""}</p>
              <p className="mt-2 text-sm text-[#3c4a41] line-clamp-3 whitespace-pre-line">{l.description}</p>
              {l.status === "approved" && (l.seller?.plan_active
                ? <Link to={`/market/${l.id}`} className="mt-2 inline-block text-xs underline text-[#3d644e]">View live</Link>
                : <p className="mt-2 text-xs text-[#6b4a14]" data-testid={`admin-market-hidden-${l.id}`}>Hidden: the seller's plan isn't active</p>)}
            </div>
            <div className="flex sm:flex-col gap-2 shrink-0">
              {l.status === "pending" && <button onClick={() => review(l.id, "approve")} className={`${btn} bg-[#1f402b] text-white border-[#1f402b]`} data-testid={`admin-market-approve-${l.id}`}>Approve</button>}
              {l.status === "pending" && <button onClick={() => review(l.id, "reject")} className={`${btn} border-[#e2dcd4]`} data-testid={`admin-market-reject-${l.id}`}>Reject</button>}
              {l.status === "approved" && <button onClick={() => review(l.id, "remove")} className={`${btn} border-[#efcfcf] text-[#7a1d1d]`} data-testid={`admin-market-remove-${l.id}`}>Take down</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
